/**
 * WHAT: DAMA PRO module for analyzing and displaying user exposure to sensitive data.
 * WHY: Security and compliance teams need visibility into which users can access
 *      sensitive classified data, enabling risk assessment and remediation.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Displays a filterable table of users and their exposure to sensitive data sources.
 * WHY: Provides a single pane for security teams to search by username, data source,
 *      or sensitivity level and trigger on-demand access analysis scans.
 */
const ProUserExposure = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        sensitivity: '',
        source_id: '',
        username: ''
    });
    const [sources, setSources] = useState([]);
    const [analyzing, setAnalyzing] = useState(false);

    useEffect(() => {
        fetchSources();
        fetchUsers();
    }, []);

    /* WHAT: Loads the list of registered data sources for the filter dropdown.
     * WHY: Users need to narrow exposure results to a specific source. */
    const fetchSources = async () => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/sources/`);
            setSources(response.data.sources || []);
        } catch (err) {
            console.error('Error fetching sources:', err);
        }
    };

    /* WHAT: Fetches user exposure records from the API, applying current filters.
     * WHY: The exposure list must reflect the active filter combination so users
     *      can drill down by username, source, or sensitivity level. */
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.sensitivity) params.append('sensitivity', filters.sensitivity);
            if (filters.source_id) params.append('source_id', filters.source_id);
            if (filters.username) params.append('username', filters.username);

            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/access/exposure/?${params}`);
            setUsers(response.data.users || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    /* WHAT: Triggers a server-side access analysis scan for the selected source.
     * WHY: Exposure data is computed on-demand because scanning all user-to-asset
     *      mappings is expensive; this lets the user control when it runs. */
    const handleAnalyze = async () => {
        setAnalyzing(true);
        try {
            const response = await axios.post(`${API_CONFIG.BASE_URL}/api/pro/access/analyze/`, {
                source_id: filters.source_id || null
            });
            alert(`Analysis complete: ${response.data.total_users || 0} users analyzed`);
            fetchUsers();
        } catch (err) {
            alert('Analysis failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setAnalyzing(false);
        }
    };

    /* WHAT: Handles filter form submission by re-fetching users.
     * WHY: Prevents default form reload and applies the current filter state. */
    const handleSearch = (e) => {
        e.preventDefault();
        fetchUsers();
    };

    /* WHAT: Resets all filter fields and refreshes the unfiltered user list.
     * WHY: Gives users a one-click way to return to the full exposure view. */
    const clearFilters = () => {
        setFilters({ sensitivity: '', source_id: '', username: '' });
        fetchUsers();
    };

    /* WHAT: Maps a sensitivity level string to a Bootstrap badge color class.
     * WHY: Visual severity encoding (red=CRITICAL, green=LOW) lets users
     *      quickly triage high-risk exposure rows in the table. */
    const getSensitivityBadge = (level) => {
        const badges = {
            'CRITICAL': 'bg-danger',
            'HIGH': 'bg-warning text-dark',
            'MEDIUM': 'bg-info',
            'LOW': 'bg-success'
        };
        return badges[level] || 'bg-secondary';
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">User Exposure Analysis</h4>
                    <p className="text-muted mb-0">
                        View users' access to sensitive data
                    </p>
                </div>
                <button
                    className="btn btn-success"
                    onClick={handleAnalyze}
                    disabled={analyzing}
                >
                    {analyzing ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Analyzing...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-search me-2"></i>
                            Analyze Access
                        </>
                    )}
                </button>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <form onSubmit={handleSearch} className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label">Username</label>
                            <input
                                type="text"
                                className="form-control"
                                value={filters.username}
                                onChange={(e) => setFilters({ ...filters, username: e.target.value })}
                                placeholder="Search username..."
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Data Source</label>
                            <select
                                className="form-select"
                                value={filters.source_id}
                                onChange={(e) => setFilters({ ...filters, source_id: e.target.value })}
                            >
                                <option value="">All Sources</option>
                                {sources.map(s => (
                                    <option key={s.source_id} value={s.source_id}>{s.source_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Min Sensitivity</label>
                            <select
                                className="form-select"
                                value={filters.sensitivity}
                                onChange={(e) => setFilters({ ...filters, sensitivity: e.target.value })}
                            >
                                <option value="">All Levels</option>
                                <option value="CRITICAL">CRITICAL</option>
                                <option value="HIGH">HIGH</option>
                                <option value="MEDIUM">MEDIUM</option>
                                <option value="LOW">LOW</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <button type="submit" className="btn btn-primary me-2">
                                <i className="fas fa-search me-1"></i>
                                Search
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                                Clear
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Results */}
            <div className="card">
                <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-user-shield me-2"></i>
                        User Exposure ({users.length} users)
                    </h5>
                </div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <table className="table table-hover mb-0">
                            <thead className="table-dark">
                                <tr>
                                    <th>Username</th>
                                    <th>Source</th>
                                    <th>User Type</th>
                                    <th>Max Sensitivity</th>
                                    <th className="text-center">Assets</th>
                                    <th>Classifications</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-muted">
                                            No users found. Run "Analyze Access" to map users to sensitive data.
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((user, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <strong>{user.username}</strong>
                                            </td>
                                            <td>
                                                {user.source_name}
                                                <span className={`badge ms-2 ${
                                                    user.source_type === 'ORACLE' ? 'bg-danger' :
                                                    user.source_type === 'MSSQL' ? 'bg-primary' :
                                                    'bg-secondary'
                                                }`}>
                                                    {user.source_type}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="badge bg-secondary">
                                                    {user.user_type || 'USER'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${getSensitivityBadge(user.max_sensitivity_level)}`}>
                                                    {user.max_sensitivity_level}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-info">{user.assets_accessible || 0}</span>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {user.classifications?.slice(0, 5).map((c, i) => (
                                                        <span key={i} className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                                                            {c.classification}
                                                        </span>
                                                    ))}
                                                    {user.classifications?.length > 5 && (
                                                        <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                                                            +{user.classifications.length - 5} more
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProUserExposure;
